import React, { useState, useEffect } from 'react';
import {
  Card,
  Radio,
  Button,
  Space,
  Table,
  Modal,
  Form,
  Input,
  Select,
  message,
  Spin,
  Empty,
  Tag,
  Divider,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  PhoneOutlined,
} from '@ant-design/icons';
import { callSettingsService } from '../services/callSettingsService';

const CallSettings = ({ organizationId }) => {
  const [provider, setProvider] = useState('plivo');
  const [countries, setCountries] = useState([]);
  const [callSettings, setCallSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [form] = Form.useForm();

  // Fetch countries
  const fetchCountries = async () => {
    setCountriesLoading(true);
    try {
      const response = await callSettingsService.getCountries();
      if (response.success) {
        setCountries(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch countries:', error);
      message.error('Failed to load countries');
    } finally {
      setCountriesLoading(false);
    }
  };

  // Fetch call settings
  const fetchCallSettings = async () => {
    setLoading(true);
    try {
      const response = await callSettingsService.getCallSettings(organizationId);
      if (response.success && response.data) {
        setCallSettings(response.data);
        setProvider(response.data.provider);
      }
    } catch (error) {
      console.error('Failed to fetch call settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
    fetchCallSettings();
  }, [organizationId]);

  // Get current provider settings
  const getCurrentSettings = () => {
    if (!callSettings) return [];
    return provider === 'plivo'
      ? callSettings.plivoSettings?.countries || []
      : callSettings.twilioSettings?.countries || [];
  };

  // Handle provider change
  const handleProviderChange = (e) => {
    setProvider(e.target.value);
  };

  // Handle add/edit country
  const handleSaveCountry = async (values) => {
    try {
      let response;
      
      console.log('Saving country configuration:', { editingCountry, values, provider });
      
      // If editing, we need to delete the old one and add the new one
      if (editingCountry) {
        console.log('Deleting old country config:', editingCountry.countryId);
        // Delete old country config
        if (provider === 'plivo') {
          const deleteResponse = await callSettingsService.removePlivoCountry(organizationId, editingCountry.countryId);
          console.log('Delete response:', deleteResponse);
        } else {
          const deleteResponse = await callSettingsService.removeTwilioCountry(organizationId, editingCountry.countryId);
          console.log('Delete response:', deleteResponse);
        }
      }
      
      // Add/update country config
      console.log('Adding new country config');
      if (provider === 'plivo') {
        response = await callSettingsService.addPlivoCountry(organizationId, values);
      } else {
        response = await callSettingsService.addTwilioCountry(organizationId, values);
      }

      console.log('Add response:', response);
      
      if (response.success) {
        message.success(editingCountry ? 'Country configuration updated successfully' : 'Country configuration saved successfully');
        setModalVisible(false);
        form.resetFields();
        setEditingCountry(null);
        // Add a small delay to ensure backend is updated
        setTimeout(() => {
          fetchCallSettings();
        }, 500);
      } else {
        message.error(response.message || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Failed to save country configuration:', error);
      message.error(
        error.response?.data?.message || error.message || 'Failed to save configuration'
      );
    }
  };

  // Handle delete country
  const handleDeleteCountry = async (countryId) => {
    Modal.confirm({
      title: 'Delete Country Configuration',
      content: 'Are you sure you want to delete this country configuration?',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          console.log('Deleting country configuration:', { countryId, provider, organizationId });
          
          let response;
          if (provider === 'plivo') {
            response = await callSettingsService.removePlivoCountry(organizationId, countryId);
          } else {
            response = await callSettingsService.removeTwilioCountry(organizationId, countryId);
          }

          console.log('Delete response:', response);
          
          if (response.success) {
            message.success('Country configuration deleted successfully');
            // Add a small delay to ensure backend is updated
            setTimeout(() => {
              fetchCallSettings();
            }, 500);
          } else {
            message.error(response.message || 'Failed to delete configuration');
          }
        } catch (error) {
          console.error('Failed to delete country configuration:', error);
          message.error(error.response?.data?.message || error.message || 'Failed to delete configuration');
        }
      },
    });
  };

  // Table columns for Plivo
  const plivoColumns = [
    {
      title: 'Country',
      dataIndex: 'countryName',
      key: 'countryName',
      render: (text, record) => (
        <Space>
          <Tag>{record.countryCode}</Tag>
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Phone Numbers',
      dataIndex: 'phoneNumbers',
      key: 'phoneNumbers',
      render: (numbers) => (
        <Space direction="vertical" size="small">
          {numbers.map((num, idx) => (
            <div key={idx}>
              <PhoneOutlined /> {num}
            </div>
          ))}
        </Space>
      ),
    },
    {
      title: 'Auth ID',
      dataIndex: 'authId',
      key: 'authId',
      render: (text) => <span>{text?.substring(0, 10)}...</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              const countryId = typeof record.countryId === 'object' 
                ? record.countryId._id || record.countryId.toString()
                : record.countryId;
              const normalizedRecord = { ...record, countryId };
              setEditingCountry(normalizedRecord);
              form.setFieldsValue(normalizedRecord);
              setModalVisible(true);
            }}
          >
            Edit
          </Button>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => {
              const countryId = typeof record.countryId === 'object' 
                ? record.countryId._id || record.countryId.toString()
                : record.countryId;
              handleDeleteCountry(countryId);
            }}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  // Table columns for Twilio
  const twilioColumns = [
    {
      title: 'Country',
      dataIndex: 'countryName',
      key: 'countryName',
      render: (text, record) => (
        <Space>
          <Tag>{record.countryCode}</Tag>
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Phone Numbers',
      dataIndex: 'phoneNumbers',
      key: 'phoneNumbers',
      render: (numbers) => (
        <Space direction="vertical" size="small">
          {numbers.map((num, idx) => (
            <div key={idx}>
              <PhoneOutlined /> {num}
            </div>
          ))}
        </Space>
      ),
    },
    {
      title: 'From Number',
      dataIndex: 'fromNumber',
      key: 'fromNumber',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              const countryId = typeof record.countryId === 'object' 
                ? record.countryId._id || record.countryId.toString()
                : record.countryId;
              const normalizedRecord = { ...record, countryId };
              setEditingCountry(normalizedRecord);
              form.setFieldsValue(normalizedRecord);
              setModalVisible(true);
            }}
          >
            Edit
          </Button>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => {
              const countryId = typeof record.countryId === 'object' 
                ? record.countryId._id || record.countryId.toString()
                : record.countryId;
              handleDeleteCountry(countryId);
            }}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const currentSettings = getCurrentSettings();

  if (loading) {
    return <Spin />;
  }

  return (
    <Card title="Call Settings" style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Provider Selection */}
        <div>
          <h3>Select Provider</h3>
          <Radio.Group value={provider} onChange={handleProviderChange}>
            <Radio value="plivo">Plivo</Radio>
            <Radio value="twilio">Twilio</Radio>
          </Radio.Group>
        </div>

        <Divider />

        {/* Add Country Button */}
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingCountry(null);
            form.resetFields();
            setModalVisible(true);
          }}
        >
          Add Country Configuration
        </Button>

        {/* Countries Table */}
        {currentSettings.length > 0 ? (
          <Table
            columns={provider === 'plivo' ? plivoColumns : twilioColumns}
            dataSource={currentSettings}
            rowKey={(record) => {
              // Use countryId as unique key - it's already unique per provider
              const countryIdStr = typeof record.countryId === 'object' 
                ? record.countryId._id || JSON.stringify(record.countryId)
                : String(record.countryId);
              return countryIdStr;
            }}
            pagination={false}
          />
        ) : (
          <Empty description="No country configurations added yet" />
        )}
      </Space>

      {/* Add/Edit Country Modal */}
      <CountryConfigModal
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingCountry(null);
          form.resetFields();
        }}
        onSave={handleSaveCountry}
        form={form}
        provider={provider}
        countries={countries}
        countriesLoading={countriesLoading}
        editingCountry={editingCountry}
      />
    </Card>
  );
};

// Country Configuration Modal Component
const CountryConfigModal = ({
  visible,
  onCancel,
  onSave,
  form,
  provider,
  countries,
  countriesLoading,
  editingCountry,
}) => {
  const [phoneNumbers, setPhoneNumbers] = useState(['']);
  const [selectedCountry, setSelectedCountry] = useState(null);

  useEffect(() => {
    if (editingCountry) {
      setPhoneNumbers(editingCountry.phoneNumbers || ['']);
      setSelectedCountry(editingCountry.countryId);
    } else {
      setPhoneNumbers(['']);
      setSelectedCountry(null);
    }
  }, [editingCountry, visible]);

  const handlePhoneNumberChange = (index, value) => {
    const newNumbers = [...phoneNumbers];
    newNumbers[index] = value;
    setPhoneNumbers(newNumbers);
  };

  const handleAddPhoneNumber = () => {
    setPhoneNumbers([...phoneNumbers, '']);
  };

  const handleRemovePhoneNumber = (index) => {
    const newNumbers = phoneNumbers.filter((_, i) => i !== index);
    setPhoneNumbers(newNumbers);
  };

  const handleCountrySelect = (countryId) => {
    setSelectedCountry(countryId);
    const country = countries.find((c) => c._id === countryId);
    if (country) {
      form.setFieldsValue({
        countryId: country._id,
        countryName: country.country_name,
        countryCode: country.country_code,
      });
    }
  };

  const handleSubmit = () => {
    // Validate that at least one phone number is provided
    const validPhoneNumbers = phoneNumbers.filter((n) => n.trim());
    if (validPhoneNumbers.length === 0) {
      message.error('Please add at least one phone number');
      return;
    }

    form.validateFields().then((values) => {
      const formData = {
        ...values,
        phoneNumbers: validPhoneNumbers,
      };
      onSave(formData);
      setPhoneNumbers(['']);
      setSelectedCountry(null);
    }).catch((err) => {
      console.error('Form validation failed:', err);
    });
  };

  return (
    <Modal
      title={editingCountry ? 'Edit Country Configuration' : 'Add Country Configuration'}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      width={700}
    >
      <Form form={form} layout="vertical">
        {/* Country Selection */}
        <Form.Item
          label="Country"
          required
          rules={[{ required: true, message: 'Please select a country' }]}
        >
          <Select
            placeholder="Search and select a country"
            loading={countriesLoading}
            onChange={handleCountrySelect}
            value={selectedCountry}
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) => {
              const label = option?.label || '';
              return typeof label === 'string' 
                ? label.toLowerCase().includes(input.toLowerCase())
                : String(label).toLowerCase().includes(input.toLowerCase());
            }}
          >
            {countries.map((country) => (
              <Select.Option 
                key={country._id} 
                value={country._id}
                label={`${country.country_name} (${country.country_code})`}
              >
                {country.country_name} ({country.country_code})
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Hidden fields for country info */}
        <Form.Item name="countryId" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="countryName" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="countryCode" hidden>
          <Input />
        </Form.Item>

        {/* Phone Numbers */}
        <Form.Item label="Phone Numbers" required>
          <Space direction="vertical" style={{ width: '100%' }}>
            {phoneNumbers.map((number, index) => (
              <Row key={index} gutter={8}>
                <Col flex="auto">
                  <Input
                    placeholder="Enter phone number"
                    value={number}
                    onChange={(e) =>
                      handlePhoneNumberChange(index, e.target.value)
                    }
                  />
                </Col>
                <Col>
                  <Button
                    danger
                    onClick={() => handleRemovePhoneNumber(index)}
                    disabled={phoneNumbers.length === 1}
                  >
                    Remove
                  </Button>
                </Col>
              </Row>
            ))}
            <Button type="dashed" onClick={handleAddPhoneNumber} block>
              + Add Phone Number
            </Button>
          </Space>
        </Form.Item>

        <Divider />

        {/* Provider-specific fields */}
        {provider === 'plivo' ? (
          <>
            <Form.Item
              name="authId"
              label="Plivo Auth ID"
              rules={[{ required: true, message: 'Auth ID is required' }]}
            >
              <Input placeholder="Enter Plivo Auth ID" />
            </Form.Item>
            <Form.Item
              name="authToken"
              label="Plivo Auth Token"
              rules={[{ required: true, message: 'Auth Token is required' }]}
            >
              <Input.Password placeholder="Enter Plivo Auth Token" />
            </Form.Item>
            <Form.Item
              name="sipUser"
              label="SIP User"
              rules={[{ required: true, message: 'SIP User is required' }]}
            >
              <Input placeholder="Enter SIP User" />
            </Form.Item>
            <Form.Item
              name="sipDomain"
              label="SIP Domain"
              rules={[{ required: true, message: 'SIP Domain is required' }]}
            >
              <Input placeholder="Enter SIP Domain" />
            </Form.Item>
            <Form.Item
              name="sipPassword"
              label="SIP Password"
              rules={[{ required: true, message: 'SIP Password is required' }]}
            >
              <Input.Password placeholder="Enter SIP Password" />
            </Form.Item>
          </>
        ) : (
          <>
            <Form.Item
              name="accountSid"
              label="Twilio Account SID"
              rules={[{ required: true, message: 'Account SID is required' }]}
            >
              <Input placeholder="Enter Twilio Account SID" />
            </Form.Item>
            <Form.Item
              name="authToken"
              label="Twilio Auth Token"
              rules={[{ required: true, message: 'Auth Token is required' }]}
            >
              <Input.Password placeholder="Enter Twilio Auth Token" />
            </Form.Item>
            <Form.Item
              name="fromNumber"
              label="From Number"
              rules={[{ required: true, message: 'From Number is required' }]}
            >
              <Input placeholder="Enter From Number" />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default CallSettings;
